// Reducer de la escena + historial (undo/redo).
//
// Acciones "discretas" (agregar/eliminar/reordenar/fondo) hacen checkpoint solas.
// Las "continuas" (drag/resize/tipeo/sliders) son TRANSIENT: el caller hace un
// CHECKPOINT al empezar la interacción y después despacha UPDATE_LAYER sin tocar
// el historial, para no floodearlo con un estado por cada pixel.

import type { Layer, Scene } from './types';

export interface SceneState {
  scene: Scene;
  past: Scene[];
  future: Scene[];
}

export type ReorderDir = 'front' | 'back' | 'forward' | 'backward';

export type SceneAction =
  | { type: 'INIT'; scene: Scene }
  | { type: 'CHECKPOINT' }
  | { type: 'ADD_LAYER'; layer: Layer }
  | { type: 'UPDATE_LAYER'; id: string; patch: Partial<Layer> }
  | { type: 'REMOVE_LAYER'; id: string }
  | { type: 'SELECT'; id: string | null }
  | { type: 'REORDER'; id: string; dir: ReorderDir }
  | { type: 'MOVE_LAYER'; from: number; to: number }
  | { type: 'SET_BG'; color: string }
  | { type: 'UNDO' }
  | { type: 'REDO' };

const MAX_HISTORY = 60;

function snapshot(state: SceneState): Pick<SceneState, 'past' | 'future'> {
  return { past: [...state.past, state.scene].slice(-MAX_HISTORY), future: [] };
}

function reorder(layers: Layer[], id: string, dir: ReorderDir): Layer[] {
  const i = layers.findIndex((l) => l.id === id);
  if (i < 0) return layers;
  const next = [...layers];
  const [item] = next.splice(i, 1);
  if (dir === 'front') next.push(item);
  else if (dir === 'back') next.unshift(item);
  else if (dir === 'forward') next.splice(Math.min(i + 1, next.length), 0, item);
  else next.splice(Math.max(i - 1, 0), 0, item);
  return next;
}

export function sceneReducer(state: SceneState, action: SceneAction): SceneState {
  switch (action.type) {
    case 'INIT':
      return { scene: action.scene, past: [], future: [] };
    case 'CHECKPOINT':
      return { ...state, ...snapshot(state) };
    case 'ADD_LAYER':
      return {
        ...snapshot(state),
        scene: {
          ...state.scene,
          layers: [...state.scene.layers, action.layer],
          selectedId: action.layer.id,
        },
      };
    case 'UPDATE_LAYER':
      return {
        ...state,
        scene: {
          ...state.scene,
          layers: state.scene.layers.map((l) =>
            l.id === action.id ? ({ ...l, ...action.patch } as Layer) : l,
          ),
        },
      };
    case 'REMOVE_LAYER': {
      const layers = state.scene.layers.filter((l) => l.id !== action.id);
      return {
        ...snapshot(state),
        scene: {
          ...state.scene,
          layers,
          selectedId:
            state.scene.selectedId === action.id ? null : state.scene.selectedId,
        },
      };
    }
    case 'SELECT':
      return { ...state, scene: { ...state.scene, selectedId: action.id } };
    case 'REORDER':
      return {
        ...snapshot(state),
        scene: {
          ...state.scene,
          layers: reorder(state.scene.layers, action.id, action.dir),
        },
      };
    case 'MOVE_LAYER': {
      const { from, to } = action;
      const layers = state.scene.layers;
      if (from === to || from < 0 || to < 0 || from >= layers.length || to >= layers.length) {
        return state;
      }
      const next = [...layers];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return { ...snapshot(state), scene: { ...state.scene, layers: next } };
    }
    case 'SET_BG':
      return { ...snapshot(state), scene: { ...state.scene, bg: action.color } };
    case 'UNDO': {
      if (state.past.length === 0) return state;
      const past = [...state.past];
      const prev = past.pop() as Scene;
      return {
        scene: prev,
        past,
        future: [state.scene, ...state.future].slice(0, MAX_HISTORY),
      };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        scene: next,
        past: [...state.past, state.scene].slice(-MAX_HISTORY),
        future: rest,
      };
    }
    default:
      return state;
  }
}
